import qs from 'qs';
import HOSTS from 'constants/hosts';
import initData from 'init-data';

/**
 * Takes an object and returns a new object without pagination properties
 * @param {object} queryParams
 * @returns {object}
 */
const stripCategoryIdFromQueryParameters = queryParams => {
    // Using object spread to exclude listed variables from 'rest'
    // eslint-disable-next-line no-unused-vars
    const { categoryId, ...rest } = queryParams;
    return rest;
};

/**
 * Takes an object and returns a new object without pagination properties
 * @param {object} queryParams
 * @returns {object}
 */
const stripPaginationFromQueryParameters = queryParams => {
    // Using object spread to exclude listed variables from 'rest'
    // eslint-disable-next-line no-unused-vars
    const { spage, paging, page, fe, ...rest } = queryParams;
    return rest;
};

/**
 * Takes an object and returns a new object without savedQueryName properties
 * @param {object} queryParams
 * @returns {object}
 */
const stripSavedQueryNameFromQueryParameters = queryParams => {
    // Using object spread to exclude listed variables from 'rest'
    // eslint-disable-next-line no-unused-vars
    const { savedQueryName, ...rest } = queryParams;
    return rest;
};

/**
 * Takes an object and returns a new object without attribute properties
 * @param {object} queryParams
 * @returns {object}
 */
const stripAttributesFromQueryParameters = queryParams => {
    // Using object spread to exclude listed variables from 'rest'
    // eslint-disable-next-line no-unused-vars
    const { attributes, ...rest } = queryParams;
    return rest;
};

/**
 * Takes an object and returns a new object without itm properties
 * @param {object} queryParams
 * @returns {object}
 */
const stripItmFromQueryParameters = queryParams => {
    // Using object spread to exclude listed variables from 'rest'
    // eslint-disable-next-line no-unused-vars
    const { itm_source, itm_medium, ...rest } = queryParams;
    return rest;
};

/**
 * Creates search string from all properties in object
 * @param {object} queryParams
 * @param {{[addQueryPrefix]: bool, [encode]: bool, [skipNulls]: bool }}
 * @returns {string}
 */
const toSearchString = (
    queryParams,
    {
        addQueryPrefix = true,
        encode = true,
        skipNulls = true,
        arrayFormat = 'repeat' //qs.stringify({ a: ['b', 'c'] }, { arrayFormat: 'repeat' }) ==> 'a=b&a=c'
    } = {}
) =>
    qs.stringify(queryParams, {
        addQueryPrefix: addQueryPrefix,
        encode: encode,
        skipNulls: skipNulls,
        arrayFormat: arrayFormat
    });

/**
 * Creates query parameters object from search string
 * @param {string} search
 */
const toQueryParameters = search =>
    qs.parse(search, { ignoreQueryPrefix: true });

/**
 * Takes an url and adds tradera base url if it is not already an absolute url
 * Removes leading slashes
 * @param {string} url
 * @returns {string}
 */
const toAbsoluteUrl = relativeUrl =>
    relativeUrl.startsWith('http')
        ? relativeUrl
        : `${HOSTS.TRADERA}${relativeUrl.replace(/^\/+/, '')}`;

/**
 * From react router location object to absolute url string
 * @param {{pathname: string, search: string}} url
 * @returns {string}
 */
const toAbsoluteUrlFromObject = url =>
    toAbsoluteUrl(
        `${url.pathname ? url.pathname : ''}${url.search ? url.search : ''}`
    );

const isAbsoluteUrl = url => new RegExp('^(?:[a-z]+:)?//', 'i').test(url);

/**
 * From URL to URL with language prefix
 * @param {*} url
 * @param {*} languageCodeIso2
 */
const toLocalizedUrl = (url, languageCodeIso2) => {
    if (
        initData.featureSwitches &&
        !initData.featureSwitches['language-prefix-request-routing']
    ) {
        return url;
    }

    const ignoredDomains = [initData.webApiUrl];

    const defaultLanguage = 'sv';
    if (
        url === null ||
        url === undefined ||
        url === '' ||
        languageCodeIso2 === defaultLanguage
    ) {
        return url;
    }

    const isIgnoredDomain = ignoredDomains.find(
        domain => url.indexOf(domain) !== -1
    );

    if (isIgnoredDomain) {
        return url;
    }

    const languagePrefix = getLanguagePrefixFromUrl(url);

    if (languagePrefix !== null) {
        return url;
    }

    if (isAbsoluteUrl(url)) {
        const loc = absoluteUrlToObject(url);
        loc.pathname = `/${languageCodeIso2}${loc.pathname}`;
        return loc.href;
    } else {
        return `/${languageCodeIso2}${url}`;
    }
};

/**
 * Helper method to make a url-string into a location object
 * @param {*} url
 */
const absoluteUrlToObject = url => {
    const l = document.createElement('a');
    l.href = url;
    return l;
};

/**
 * Returns language parameter from url
 * @param {*} url
 */
const getLanguagePrefixFromUrl = url => {
    const supportedLanguages = ['en'];
    const matches = url.match(/\/([a-z][a-z])\//);

    if (matches === null) {
        return null;
    }

    if (supportedLanguages.indexOf(matches[1]) !== -1) {
        return matches[1];
    }

    return null;
};

export {
    toAbsoluteUrl,
    toAbsoluteUrlFromObject,
    toQueryParameters,
    toSearchString,
    stripPaginationFromQueryParameters,
    stripCategoryIdFromQueryParameters,
    stripItmFromQueryParameters,
    stripAttributesFromQueryParameters,
    stripSavedQueryNameFromQueryParameters,
    getLanguagePrefixFromUrl,
    toLocalizedUrl,
    isAbsoluteUrl
};
